import React from 'react';

import { lumappsMicroApp } from '@lumapps/lumx/custom-icons';
import { Emphasis, Tooltip } from '@lumapps/lumx/react';
import { getIconFromType } from '@lumapps/search-results/helpers';
import { useTranslate } from '@lumapps/translations';
import { useDynamicIcon } from '@lumapps/utils/hooks/useDynamicIcon';

import { AskAiSource, AskAiDocumentTypes, MAPPING_DOCUMENT_TYPES_TO_DEFAULT_TITLES } from '../../types';
import { formatSearchResultUrl } from '../../utils';
import { AskAiSourceChip as AskAiSourceChipUI } from './AskAiSourceChip';

interface AskAiSourceChipProps {
    source: AskAiSource;
    className?: string;
}

/**
 * Display source as chip
 * @param source source to be displayed
 * @param className classname to be used for chip
 * @returns a chip for a source with a tooltip, an icon, source title and index
 */
export const AskAiSourceChip = ({ source, className }: AskAiSourceChipProps) => {
    const { translateKey } = useTranslate();

    const { title, type, mimeType, url, icon } = source;
    const sourceTitle = title || translateKey(MAPPING_DOCUMENT_TYPES_TO_DEFAULT_TITLES[type]);

    const isContent = type === AskAiDocumentTypes.CONTENT;
    const isDirectoryEntryMicroApp = type === AskAiDocumentTypes.DIRECTORY_ENTRY_MICROAPP;

    // We retrieve the icon based on the type of document and add for genericDocument or media a color if applicable.
    const shouldUseMimeType =
        (type === AskAiDocumentTypes.GENERIC_DOCUMENT || type === AskAiDocumentTypes.MEDIA) && mimeType;

    let sourceIcon = '';
    if (isContent && icon) {
        sourceIcon = icon;
    } else if (shouldUseMimeType) {
        sourceIcon = getIconFromType(mimeType);
    } else {
        sourceIcon = getIconFromType(type);
    }
    const { iconPath } = useDynamicIcon({ icon: sourceIcon });

    // icon for directory as microapp is a custom icon so we already have the SVG on our side
    const finalIcon = isDirectoryEntryMicroApp ? lumappsMicroApp : iconPath;

    return (
        <Tooltip label={sourceTitle} placement="top">
            <AskAiSourceChipUI
                className={className}
                leftIcon={finalIcon}
                emphasis={Emphasis.low}
                linkAs="a"
                href={formatSearchResultUrl(url)}
                target="_blank"
            >
                {sourceTitle}
            </AskAiSourceChipUI>
        </Tooltip>
    );
};
